// src/lib/utils/colors/harmony.ts
//
// Pure harmony-palette orchestrator harvested from ColorPaletteGenerator.tsx
// (A11/C1). It previously sat untested inside a 1,892-line 'use client'
// component; here it is framework-free and unit-tested. Delegates to the
// existing transforms / color-relationships primitives.
import { createDeterministicRandom, hashSeed } from '~/lib/utils/random'
import { parseColorString, rgbToHsl, rgbToHex, hslToRgb } from './transforms'
import {
  getTonalColor,
  getComplementary,
  getTriadic,
  getTetradic,
  getSplitComplementary,
  getAnalogous,
  getRectangle,
  withSaturation,
  withLightness,
} from './color-relationships'
import type { HarmonyType } from './types'

/**
 * Generate a harmony palette of the requested size.
 *
 * For a finite `count >= 2` and a parseable `baseColor`, returns exactly
 * `count` colors (base first), using the harmony type for relationships and
 * filling any remaining slots deterministically. For a non-finite/`< 2` count,
 * or a `baseColor` that `parseColorString` can't read, it returns the
 * single-element fallback `[baseColor]` — a harmony can't be derived from an
 * unparseable color, and the sole consumer (`ColorPaletteGenerator`) only ever
 * calls this with a validated hex base and an integer palette size of 3–6.
 *
 * @param baseColor - The user's base color (never modified)
 * @param harmonyType - Type of harmony to generate
 * @param count - Number of colors to generate (including the base color)
 * @param saturationPref - Saturation preference (`'original'` | `'muted'` | `'balanced'` | `'vibrant'` | `'intense'`)
 * @param lightnessPref - Lightness preference (`'original'` | `'dark'` | `'medium'` | `'light'`)
 * @returns `count` colors for valid input, else the `[baseColor]` fallback
 */
export function generateHarmonyColors(
  baseColor: string,
  harmonyType: HarmonyType,
  count: number,
  saturationPref: string,
  lightnessPref: string
): string[] {
  // Guard the now-public boundary: a non-finite or < 2 count can't yield a
  // multi-color harmony (NaN would otherwise slip past the old `<= 1` check and
  // produce an empty `slice(0, NaN)`). Unreachable from the UI (integer 3–6).
  if (!Number.isFinite(count) || count <= 1) return [baseColor]
  
  const colors: string[] = [baseColor]
  const baseRgb = parseColorString(baseColor)
  if (!baseRgb) return [baseColor]
  
  const baseHsl = rgbToHsl(baseRgb)
  const [, baseS, baseL] = baseHsl
  
  // Generate initial harmony colors based on harmony type
  let harmonyColors: string[] = []
  
  switch (harmonyType) {
    case 'monochromatic':
      // Generate tonal variations
      for (let i = 1; i < count; i++) {
        const t = i / Math.max(1, count - 1)
        harmonyColors.push(getTonalColor(baseColor, t, [15, 85]))
      }
      break
      
    case 'complementary':
      harmonyColors = [getComplementary(baseColor)]
      break
      
    case 'triadic':
      harmonyColors = getTriadic(baseColor)
      break
      
    case 'tetradic':
      harmonyColors = getTetradic(baseColor)
      break
      
    case 'split-complementary':
      harmonyColors = getSplitComplementary(baseColor, 30)
      break
      
    case 'analogous':
      harmonyColors = getAnalogous(baseColor, 30)
      break
      
    case 'square':
      harmonyColors = getTetradic(baseColor)
      break
      
    case 'double-complementary':
    case 'rectangle':
      harmonyColors = getRectangle(baseColor)
      break
      
    default:
      harmonyColors = []
  }
  
  // Add harmony colors to our palette
  colors.push(...harmonyColors)
  
  // If we need more colors to reach the requested count, generate additional ones
  while (colors.length < count) {
    const baseHue = baseHsl[0]
    const hueStep = 360 / count
    const newHue = (baseHue + (colors.length * hueStep)) % 360
    
    // Generate a new color with the calculated hue
    const newColor = rgbToHex(hslToRgb([newHue, baseS, baseL]))
    colors.push(newColor)
  }
  
  // Remove duplicates while preserving order
  const uniqueColors: string[] = []
  for (const color of colors) {
    if (!uniqueColors.includes(color)) {
      uniqueColors.push(color)
    }
  }
  
  // If we still don't have enough colors, generate more using different strategies
  // Seed a deterministic RNG from the base color so identical input always
  // yields the same palette (Math.random() made the fallback fill non-reproducible).
  const fillRng = createDeterministicRandom(hashSeed(baseColor))
  // Bounded fill: the random-hue fill can collide with existing colors, so cap
  // iterations to avoid a theoretical main-thread hang on a pathological seed.
  // The cap is generous (100× count) so realistic counts (3–6) always converge
  // and reach the requested size before it trips.
  let fillGuard = 0
  const maxFillIterations = count * 100
  while (uniqueColors.length < count && fillGuard++ < maxFillIterations) {
    const baseHue = baseHsl[0]
    const randomHue = (baseHue + fillRng() * 360) % 360
    const randomSat = Math.max(30, Math.min(90, baseS + (fillRng() - 0.5) * 40))
    const randomLight = Math.max(20, Math.min(80, baseL + (fillRng() - 0.5) * 40))
    
    const newColor = rgbToHex(hslToRgb([randomHue, randomSat, randomLight]))
    if (!uniqueColors.includes(newColor)) {
      uniqueColors.push(newColor)
    }
  }
  
  // Apply user preferences to each color (except the base color)
  return uniqueColors.slice(0, count).map((color, index) => {
    if (index === 0) return color // Don't modify the base color
    
    let adjustedColor = color
    
    // Apply saturation preference
    if (saturationPref !== 'original') {
      const rgb = parseColorString(adjustedColor)
      if (rgb) {
        const hsl = rgbToHsl(rgb)
        let newS = hsl[1]
        
        switch (saturationPref) {
          case 'muted':
            newS = Math.max(0, newS * 0.4)
            break
          case 'balanced':
            newS = Math.max(0, newS * 0.7)
            break
          case 'vibrant':
            newS = Math.min(100, newS * 1.2)
            break
          case 'intense':
            newS = Math.min(100, newS * 1.4)
            break
          default:
            throw new Error('Unknown saturation preference')
        }
        
        adjustedColor = withSaturation(adjustedColor, newS)
      }
    }
    
    // Apply lightness preference
    if (lightnessPref !== 'original') {
      const rgb = parseColorString(adjustedColor)
      if (rgb) {
        const hsl = rgbToHsl(rgb)
        let newL = hsl[2]
        
        switch (lightnessPref) {
          case 'dark':
            newL = Math.max(10, newL * 0.5)
            break
          case 'medium':
            // Keep original lightness
            break
          case 'light':
            newL = Math.min(90, newL * 1.3)
            break
          default:
            throw new Error('Unknown lightness preference')
        }
        
        adjustedColor = withLightness(adjustedColor, newL)
      }
    }
    
    return adjustedColor
  })
}
